import { type Business, type LensSDKException, NotFoundException } from "lens-platform-sdk";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "src/App";
import { useCallback, useMemo } from "react";
import { useLensPlatformClient } from "./useLensPlatformClient";
import { useAnalytics } from "./useAnalytics";

/**
 * Hooks for read and delete business (Lens Business IDs) parent.
 */
export const useBusinessParent = (businessId?: Business["id"]) => {
  const lensPlatformClient = useLensPlatformClient();
  const { track } = useAnalytics();

  // See https://tkdodo.eu/blog/react-query-and-type-script#type-safety-with-the-enabled-option
  const fetchParent = useCallback((id?: Business["id"]) => (typeof id === "undefined"
    ? Promise.reject(new Error("Invalid businessId"))
    : lensPlatformClient.business.getParent(id)), [lensPlatformClient]);

  const {
    isLoading: loadingBusinessParent,
    error: errorLoadingBusinessParent,
    data: businessParent,
    refetch: refetchBusinessParent
  } = useQuery<Business, LensSDKException>({
    queryKey: ["businessParent", businessId],
    retry: (_, error) => {
      if (error instanceof NotFoundException) {
        return false;
      }

      return true;
    },
    queryFn: () => fetchParent(businessId)
  });

  const {
    isLoading: removingBusinessParent,
    isSuccess: removingBusinessParentSuccess,
    error: errorRemovingBusinessParent,
    mutate: removeBusinessParent
  } = useMutation<void, LensSDKException, Business["id"]>(
    async (childBusinessId: Business["id"]) => lensPlatformClient.business.removeParent(childBusinessId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["businessParent", businessId] });
        track("BusinessID parent removed", { businessId });
      }
    }
  );

  return {
    loadingBusinessParent,
    errorLoadingBusinessParent,
    // Ensure to return undefined if the business parent is not found
    businessParent: useMemo(() => {
      if (errorLoadingBusinessParent instanceof NotFoundException) {
        return undefined;
      }

      return businessParent;
    }, [businessParent, errorLoadingBusinessParent]),
    refetchBusinessParent,
    removingBusinessParent,
    removingBusinessParentSuccess,
    errorRemovingBusinessParent,
    removeBusinessParent
  };
};
